// Copyright 2026 AllSpice. All rights reserved.

import {defineStore} from 'pinia';
import {ref, type ComputedRef} from 'vue';
import type {MultiPageDiff, PreviewableTreeNode, ProjectPageDiff} from '../types/file_tree.ts';
import {FILE_DIFF_STATUS} from '../types/file_tree.ts';
import {getParentHash} from '../utils/multi-page.ts';
import {
  createDefaultMultiPageSearchState,
  createEmptyMultiPageComponentIndex,
  type ComponentInstance,
  type MultiPageComponentIndex,
  type MultiPageSearchState,
  type NetInstance,
} from './multiPage/search.ts';
import {clusterWires, groupWiresByNet, type NetWire} from '../../utils/wire-cluster.ts';
import {AllSpiceJsonLoader} from '../../features/allspicejson.ts';
import {isNetSearchDisabledSchematic} from '../../utils/schematic-format.ts';
import type {Container} from '../../container.ts';
import type {DiffInfo} from '../store.ts';

type MultiPageSearchContext = {
  hashToPathMap: ComputedRef<Map<string, PreviewableTreeNode>>;
  container: Container;
  diffInfo: DiffInfo;
};

type SearchablePage = {
  components?: Record<string, {reference?: string}>;
  wires?: Record<string, NetWire>;
};

type PageJson = {id: string; components?: Record<string, {reference?: string}>};

function toErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'string') {
    return error;
  }

  return 'Failed to load component index';
}

function buildReferenceIndex(instances: ComponentInstance[]): Map<string, ComponentInstance[]> {
  const byReference = new Map<string, ComponentInstance[]>();

  for (const instance of instances) {
    const existing = byReference.get(instance.reference);
    if (existing) {
      existing.push(instance);
      continue;
    }

    byReference.set(instance.reference, [instance]);
  }

  return byReference;
}

function sortInstances(instances: ComponentInstance[]): ComponentInstance[] {
  return instances.sort((left, right) => {
    const referenceComparison = left.reference.localeCompare(right.reference, undefined, {numeric: true, sensitivity: 'base'});
    if (referenceComparison !== 0) {
      return referenceComparison;
    }

    return left.pageOrder - right.pageOrder;
  });
}

function findMultiPageParent(file: ProjectPageDiff, hashToPathMap: Map<string, PreviewableTreeNode>): MultiPageDiff | null {
  const parent = hashToPathMap.get(getParentHash(file.nameHash));
  if (parent?.ty === 'multi-page-diff') {
    return parent;
  }

  return null;
}

function collectInstancesFromPage(
  page: SearchablePage | undefined,
  child: ProjectPageDiff,
  pageOrder: number,
  seen: Set<string>,
  instances: ComponentInstance[],
  skippedReferences: Set<string> = new Set(),
): void {
  if (!page?.components) {
    return;
  }

  for (const [componentId, componentData] of Object.entries(page.components)) {
    const reference = componentData?.reference;
    if (!reference) {
      continue;
    }

    if (skippedReferences.has(reference)) {
      continue;
    }

    const dedupeKey = `${child.pageId}::${componentId}`;
    if (seen.has(dedupeKey)) {
      continue;
    }

    seen.add(dedupeKey);
    instances.push({
      pageId: child.pageId,
      pageOrder,
      pageName: child.name,
      pageNameHash: child.nameHash,
      componentId,
      reference,
    });
  }
}

function collectReferences(page: SearchablePage | undefined): Set<string> {
  if (!page?.components) {
    return new Set();
  }

  return new Set(
    Object.values(page.components)
      .map((component) => component?.reference)
      .filter(Boolean) as string[],
  );
}

function collectInstancesFromJson(
  children: ProjectPageDiff[],
  headPages: PageJson[],
  basePages: PageJson[],
): ComponentInstance[] {
  const instances: ComponentInstance[] = [];
  const seen = new Set<string>();
  const headMap = new Map(headPages.map((p) => [p.id, p]));
  const baseMap = new Map(basePages.map((p) => [p.id, p]));

  for (let pageOrder = 0; pageOrder < children.length; pageOrder++) {
    const child = children[pageOrder];
    const headPage = headMap.get(child.pageId);
    const basePage = baseMap.get(child.basePageId);
    const headReferences = collectReferences(headPage);

    collectInstancesFromPage(headPage, child, pageOrder, seen, instances);
    collectInstancesFromPage(basePage, child, pageOrder, seen, instances, headReferences);
  }

  return sortInstances(instances);
}

type JsonPage = {id: string; wires?: Record<string, NetWire>};

function collectNetInstancesFromJson(
  children: ProjectPageDiff[],
  allspiceJson: {headJson: Record<string, unknown>; baseJson: Record<string, unknown>},
): {netInstances: NetInstance[]; byNet: Map<string, NetInstance[]>} {
  const nets: Record<string, {id: string; name: string}> = {
    ...((allspiceJson.baseJson?.nets ?? {}) as Record<string, {id: string; name: string}>),
    ...((allspiceJson.headJson?.nets ?? {}) as Record<string, {id: string; name: string}>),
  };
  if (Object.keys(nets).length === 0) {
    return {netInstances: [], byNet: new Map()};
  }

  const headPages = ((allspiceJson.headJson?.pages ?? []) as JsonPage[]);
  const basePages = ((allspiceJson.baseJson?.pages ?? []) as JsonPage[]);
  const headMap = new Map(headPages.map((p) => [p.id, p]));
  const baseMap = new Map(basePages.map((p) => [p.id, p]));

  const netInstances: NetInstance[] = [];
  const seen = new Set<string>();

  for (let pageOrder = 0; pageOrder < children.length; pageOrder++) {
    const child = children[pageOrder];
    const headPage = headMap.get(child.pageId);
    const basePage = baseMap.get(child.basePageId);
    if (!headPage && !basePage) continue;

    // Merge wires from both sides; head wires take priority via insertion order.
    const mergedWires: Record<string, NetWire> = {};
    for (const [id, wire] of Object.entries(basePage?.wires ?? {})) {
      mergedWires[id] = wire;
    }
    for (const [id, wire] of Object.entries(headPage?.wires ?? {})) {
      mergedWires[id] = wire;
    }

    const wiresByNet = groupWiresByNet(mergedWires, nets);

    for (const [netId, netWires] of wiresByNet) {
      const net = nets[netId];
      const clusters = clusterWires(netWires);

      for (const clusterIds of clusters) {
        const dataSelector = clusterIds
          .map((id) => `[data-id='${CSS.escape(id)}']`)
          .join(', ');

        const dedupeKey = `${child.pageId}::${dataSelector}`;
        if (seen.has(dedupeKey)) continue;
        seen.add(dedupeKey);

        netInstances.push({
          pageId: child.pageId,
          pageOrder,
          pageName: child.name,
          pageNameHash: child.nameHash,
          netId,
          netName: net.name ?? netId,
          wireIds: clusterIds,
          dataSelector,
        });
      }
    }
  }

  netInstances.sort((a, b) => {
    const nameComp = a.netName.localeCompare(b.netName, undefined, {numeric: true, sensitivity: 'base'});
    if (nameComp !== 0) return nameComp;
    return a.pageOrder - b.pageOrder;
  });

  const byNet = new Map<string, NetInstance[]>();
  for (const inst of netInstances) {
    const existing = byNet.get(inst.netName);
    if (existing) existing.push(inst);
    else byNet.set(inst.netName, [inst]);
  }

  return {netInstances, byNet};
}

async function buildMultiPageComponentIndex(file: ProjectPageDiff | MultiPageDiff, context: MultiPageSearchContext): Promise<MultiPageComponentIndex> {
  const parent = file.ty === 'multi-page-diff' ? file : findMultiPageParent(file, context.hashToPathMap.value);
  if (!parent?.children?.length) {
    return createEmptyMultiPageComponentIndex({
      error: 'Could not find sibling pages for this file',
    });
  }

  const {container, diffInfo} = context;

  const headUrl = AllSpiceJsonLoader.buildUrl(
    container, diffInfo.username, diffInfo.reponame, diffInfo.headCommit, file.headPath,
  );
  let baseUrl = AllSpiceJsonLoader.buildUrl(
    container, diffInfo.username, diffInfo.reponame, diffInfo.baseCommit, file.basePath,
  );
  if (parent.status === FILE_DIFF_STATUS.ADDED) {
    baseUrl = null;
  }

  const allspiceJson = await AllSpiceJsonLoader.load(container, headUrl, baseUrl);

  const headPages = (allspiceJson.headJson?.pages || []) as PageJson[];
  const basePages = (allspiceJson.baseJson?.pages || []) as PageJson[];

  const instances = collectInstancesFromJson(parent.children, headPages, basePages);
  const {netInstances, byNet} = isNetSearchDisabledSchematic(file.headPath) ?
    {netInstances: [], byNet: new Map<string, NetInstance[]>()} :
    collectNetInstancesFromJson(parent.children, allspiceJson);
  return createEmptyMultiPageComponentIndex({
    instances,
    byReference: buildReferenceIndex(instances),
    netInstances,
    byNet,
  });
}

export const useMultiPageSearchStore = defineStore('multiPageSearch', () => {
  const componentIndexes = ref<Record<string, MultiPageComponentIndex>>({});
  const searchStates = ref<Record<string, MultiPageSearchState>>({});
  const pendingIndexes = new Map<string, Promise<MultiPageComponentIndex>>();

  function getComponentIndex(headPath: string): MultiPageComponentIndex | null {
    return componentIndexes.value[headPath] ?? null;
  }

  function getSearchState(headPath: string): MultiPageSearchState {
    return searchStates.value[headPath] ?? createDefaultMultiPageSearchState();
  }

  function setComponentIndex(headPath: string, index: MultiPageComponentIndex): void {
    componentIndexes.value = {
      ...componentIndexes.value,
      [headPath]: index,
    };
  }

  function updateSearchState(headPath: string, patch: Partial<MultiPageSearchState>): void {
    searchStates.value = {
      ...searchStates.value,
      [headPath]: {
        ...getSearchState(headPath),
        ...patch,
      },
    };
  }

  function setSearchQuery(headPath: string, searchQuery: string): void {
    updateSearchState(headPath, {searchQuery});
  }

  function setSelection(headPath: string, selectedReference: string | null, selectedCategory: 'Reference' | 'Net' | null, selectedIndex: number): void {
    updateSearchState(headPath, {selectedReference, selectedCategory, selectedIndex});
  }

  function setOpenPage(headPath: string, openPageId: string | null): void {
    updateSearchState(headPath, {openPageId});
  }

  function resetSearchState(headPath: string): void {
    searchStates.value = {
      ...searchStates.value,
      [headPath]: createDefaultMultiPageSearchState(),
    };
  }

  async function ensureComponentIndex(file: ProjectPageDiff | MultiPageDiff, context: MultiPageSearchContext): Promise<MultiPageComponentIndex> {
    const cacheKey = file.headPath;
    const existing = getComponentIndex(cacheKey);
    if (existing && !existing.loading && !existing.error) {
      return existing;
    }

    const pending = pendingIndexes.get(cacheKey);
    if (pending !== undefined) {
      return pending;
    }

    setComponentIndex(cacheKey, createEmptyMultiPageComponentIndex({loading: true}));

    const loadPromise = (async () => {
      try {
        const index = await buildMultiPageComponentIndex(file, context);
        setComponentIndex(cacheKey, index);
        return index;
      } catch (error: unknown) {
        console.error('Failed to build multi-page component index for', file.headPath, error);
        const errorIndex = createEmptyMultiPageComponentIndex({error: toErrorMessage(error)});
        setComponentIndex(cacheKey, errorIndex);
        return errorIndex;
      } finally {
        pendingIndexes.delete(cacheKey);
      }
    })();

    pendingIndexes.set(cacheKey, loadPromise);
    return loadPromise;
  }

  function $reset(): void {
    componentIndexes.value = {};
    searchStates.value = {};
    pendingIndexes.clear();
  }

  return {
    componentIndexes,
    searchStates,
    getComponentIndex,
    getSearchState,
    setSearchQuery,
    setSelection,
    setOpenPage,
    resetSearchState,
    ensureComponentIndex,
    $reset,
  };
});

export type {MultiPageSearchContext};
